/**
 * LocalLibraryAdapter.ts — browser-local library backed by `StorageBackend`.
 *
 * Storage layout (in the existing prefs/bundle storage the app already owns):
 *   bs.library         JSON: { entries: LocalStoredEntry[] } — the index
 *   bs.bundle.<uuid>   JSON: ScenarioBundle  (now keyed by UUID, not slug)
 *
 * The OLD layout (pre-uuid) stored bundles under `bs.bundle.<slug>` and kept
 * ids in `bs.ids`. On first `list()`, those are migrated: each slug becomes
 * a stable entry whose `id` equals its slug (so the bundle moves from
 * `bs.bundle.<slug>` to `bs.bundle.<slug>` — already correct), and gets a
 * defaulted createdAt/updatedAt. The legacy `bs.ids` list is left in place
 * so a downgrade still finds the bundles; new writes only touch `bs.library`.
 */
import type { ScenarioBundle } from '../engine/types';
import type { StorageBackend } from '../storage/StorageBackend';
import { LibraryAdapter, LibraryEntry, LibraryError } from './LibraryAdapter';
import { slugify, uniqueSlug, uuid } from './ids';

/** Index entry persisted in `bs.library`. Carries the bits the list view needs
 *  without loading the bundle. */
interface LocalStoredEntry {
  id: string;
  slug: string;
  label: string;
  description?: string;
  tags: string[];
  nodeCount: number;
  updatedAt: number;
  createdAt?: number;
}

const LIBRARY_KEY = 'bs.library';
const LEGACY_IDS_KEY = 'bs.ids';

export class LocalLibraryAdapter implements LibraryAdapter {
  constructor(private readonly storage: StorageBackend) {}

  // ── Read ────────────────────────────────────────────────────────────────
  async list(): Promise<LibraryEntry[]> {
    this.migrateLegacy();
    const saved = this.readIndex().map((e) => this.toEntry(e));
    saved.sort((a, b) => b.updatedAt - a.updatedAt);
    return saved;
  }

  async search(query: string): Promise<LibraryEntry[]> {
    const all = await this.list();
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((e) =>
      `${e.label} ${e.description ?? ''} ${e.slug} ${e.tags.join(' ')}`.toLowerCase().includes(q),
    );
  }

  async get(id: string): Promise<ScenarioBundle | null> {
    return this.storage.loadBundle(id);
  }

  // ── Write ───────────────────────────────────────────────────────────────
  async save(bundle: ScenarioBundle, opts?: { slug?: string; tags?: string[] }): Promise<LibraryEntry> {
    this.migrateLegacy();
    const index = this.readIndex();
    const existing = index.find((e) => e.id === bundle.id);
    const requestedSlug = opts?.slug ?? existing?.slug ?? slugify(bundle.label);
    const slug = uniqueSlug(this.takenSlugsExcluding(index, existing?.id), requestedSlug);

    // Slug collision against a DIFFERENT entry → reject.
    const clash = index.find((e) => e.slug === slug && e.id !== bundle.id);
    if (clash) {
      throw new LibraryError('collision', `A file named "${slug}" already exists.`);
    }

    const now = Date.now();
    const stored: LocalStoredEntry = {
      id: bundle.id,
      slug,
      label: bundle.label,
      description: bundle.description,
      tags: opts?.tags ?? existing?.tags ?? [],
      nodeCount: bundle.graph.nodes.length,
      updatedAt: now,
      createdAt: existing?.createdAt ?? now,
    };
    this.storage.saveBundle(bundle);
    this.writeIndex(upsert(index, stored));
    return this.toEntry(stored);
  }

  async rename(id: string, label: string): Promise<LibraryEntry> {
    this.migrateLegacy();
    const index = this.readIndex();
    const entry = index.find((e) => e.id === id);
    if (!entry) throw new LibraryError('not-found', `No file with id "${id}".`);
    const trimmed = label.trim();
    if (!trimmed) throw new LibraryError('invalid', 'Name cannot be empty.');

    // Re-derive the slug from the new label; keep it unique.
    const newSlug = uniqueSlug(this.takenSlugsExcluding(index, id), slugify(trimmed));
    const clash = index.find((e) => e.slug === newSlug && e.id !== id);
    if (clash) throw new LibraryError('collision', `"${newSlug}" is already taken.`);

    const bundle = this.storage.loadBundle(id);
    if (!bundle) throw new LibraryError('not-found', `File payload missing for "${id}".`);
    const renamedBundle: ScenarioBundle = { ...bundle, label: trimmed };
    this.storage.saveBundle(renamedBundle);
    const next: LocalStoredEntry = { ...entry, label: trimmed, slug: newSlug };
    this.writeIndex(upsert(index, next));
    return this.toEntry(next);
  }

  async setTags(id: string, tags: string[]): Promise<LibraryEntry> {
    this.migrateLegacy();
    const index = this.readIndex();
    const entry = index.find((e) => e.id === id);
    if (!entry) throw new LibraryError('not-found', `No file with id "${id}".`);
    const next: LocalStoredEntry = { ...entry, tags: normalizeTags(tags) };
    this.writeIndex(upsert(index, next));
    return this.toEntry(next);
  }

  async delete(id: string): Promise<void> {
    this.migrateLegacy();
    const index = this.readIndex().filter((e) => e.id !== id);
    this.writeIndex(index);
    this.storage.deleteBundle(id);
  }

  async importBundle(bundle: ScenarioBundle, opts?: { slug?: string; tags?: string[] }): Promise<LibraryEntry> {
    this.migrateLegacy();
    const index = this.readIndex();
    const id = uuid();
    const slug = uniqueSlug(
      this.takenSlugsExcluding(index),
      opts?.slug ?? slugify(bundle.label),
    );
    const now = Date.now();
    const imported: ScenarioBundle = { ...bundle, id };
    const stored: LocalStoredEntry = {
      id,
      slug,
      label: bundle.label,
      description: bundle.description,
      tags: normalizeTags(opts?.tags ?? []),
      nodeCount: bundle.graph.nodes.length,
      updatedAt: now,
      createdAt: now,
    };
    this.storage.saveBundle(imported);
    this.writeIndex(upsert(index, stored));
    return this.toEntry(stored);
  }

  // ── Internals ───────────────────────────────────────────────────────────
  /** Convert a persisted index entry to the public LibraryEntry shape. */
  private toEntry(e: LocalStoredEntry): LibraryEntry {
    return {
      id: e.id,
      slug: e.slug,
      label: e.label,
      description: e.description,
      tags: e.tags ?? [],
      nodeCount: e.nodeCount,
      updatedAt: e.updatedAt,
      createdAt: e.createdAt,
      source: 'local',
    };
  }

  private readIndex(): LocalStoredEntry[] {
    const raw = this.storage.get<{ entries: LocalStoredEntry[] } | null>(LIBRARY_KEY, null);
    if (!raw || !Array.isArray(raw.entries)) return [];
    return raw.entries.filter((e) => e && typeof e.id === 'string');
  }

  private writeIndex(entries: LocalStoredEntry[]): void {
    this.storage.set(LIBRARY_KEY, { entries });
  }

  /** Slugs already in use, optionally excluding one entry's id (for self
   *  collision checks during save/rename). */
  private takenSlugsExcluding(index: LocalStoredEntry[], excludeId?: string): string[] {
    return index.filter((e) => e.id !== excludeId).map((e) => e.slug);
  }

  /** One-shot migration from the legacy `bs.ids` + `bs.bundle.<slug>` layout
   *  to the new uuid-indexed `bs.library`. Idempotent: marks migration done
   *  so it never runs twice. */
  private migrateLegacy(): void {
    if (this.storage.get<boolean>('bs.library.migrated', false)) return;
    const legacyIds = this.storage.get<string[] | null>(LEGACY_IDS_KEY, null);
    if (legacyIds && Array.isArray(legacyIds)) {
      const existing = this.readIndex();
      const known = new Set(existing.map((e) => e.id));
      const now = Date.now();
      for (const slug of legacyIds) {
        if (!slug || known.has(slug)) continue;
        const bundle = this.storage.loadBundle(slug);
        // Only index bundles we can actually read; orphans are ignored.
        if (!bundle) continue;
        existing.push({
          id: slug, // legacy id stays the slug — stable across downgrade
          slug,
          label: bundle.label ?? slug,
          description: bundle.description,
          tags: [],
          nodeCount: bundle.graph?.nodes?.length ?? 0,
          updatedAt: bundle.updatedAt ?? now,
          createdAt: bundle.updatedAt,
        });
      }
      this.writeIndex(existing);
    }
    this.storage.set('bs.library.migrated', true);
  }
}

/** Immutable upsert into a stored-entry index. */
function upsert(index: LocalStoredEntry[], next: LocalStoredEntry): LocalStoredEntry[] {
  const without = index.filter((e) => e.id !== next.id);
  return [...without, next];
}

/** Normalize raw tag input into a de-duplicated lowercase list. Trims, drops
 *  empties, and collapses the plural/singular duplicates the user might type
 *  inconsistently. */
function normalizeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const t = String(raw).trim().toLowerCase().replace(/\s+/g, '-');
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}
